import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Search, RefreshCw, Filter, X, Database, Play, FileText, Copy, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

// Indian number formatting helper
const formatIndianNumber = (num) => {
  if (num === null || num === undefined) return '0';
  const numStr = num.toString();
  const lastThree = numStr.slice(-3);
  const otherNumbers = numStr.slice(0, -3);
  if (otherNumbers !== '') {
    return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
  }
  return lastThree;
};

const AllUSNDetailsReport = () => {
  const navigate = useNavigate();
  
  // Data state
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 100;
  
  // Stats state
  const [globalStats, setGlobalStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Migration state
  const [migrateDialogOpen, setMigrateDialogOpen] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  
  // Duplicate export state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateStats, setDuplicateStats] = useState(null);
  const [loadingDuplicateStats, setLoadingDuplicateStats] = useState(false);
  const [exportingDuplicates, setExportingDuplicates] = useState(false);
  const [currentExportPage, setCurrentExportPage] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    usn_code: '',
    bundle_barcode: '',
    sort_no: '',
    fg_barcode: '',
    design_name: '',
    shade: '',
    size: '',
    source: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Source options
  const sourceOptions = [
    { value: '', label: 'All Sources' },
    { value: 'Greige', label: 'Greige' },
    { value: 'FOB', label: 'FOB' },
    { value: 'GT - Sort No', label: 'GT - Sort No' },
    { value: 'GT - FG', label: 'GT - FG' },
    { value: 'GT - Label', label: 'GT - Label' }
  ];

  const fetchStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const response = await apiClient.get('/admin/all-usn-details/stats');
      if (response.data.success) {
        setGlobalStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', (page * limit).toString());
      params.append('limit', limit.toString());
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await apiClient.get(`/admin/all-usn-details/report?${params.toString()}`);
      
      if (response.data.success) {
        setRecords(response.data.records || []);
        setTotal(response.data.total || 0);
        setStats(response.data.stats || {});
      }
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchRecords();
  }, [page]);

  const handleSearch = () => {
    setPage(0);
    fetchRecords();
  };

  const handleClearFilters = () => {
    setFilters({
      usn_code: '',
      bundle_barcode: '',
      sort_no: '',
      fg_barcode: '',
      design_name: '',
      shade: '',
      size: '',
      source: ''
    });
  };

  const handleMigrate = async () => {
    setIsMigrating(true);
    try {
      const response = await apiClient.post('/admin/all-usn-details/migrate');
      if (response.data.success) {
        toast.success(`Migration completed: ${response.data.migrated} records processed`);
        setMigrateDialogOpen(false);
        fetchStats();
        fetchRecords();
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast.error(error.response?.data?.detail || 'Migration failed');
    } finally {
      setIsMigrating(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await apiClient.get(`/admin/all-usn-details/export?${params.toString()}`);
      
      if (response.data.success) {
        const blob = new Blob([response.data.csv_data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `all_usn_details_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        toast.success(`Exported ${response.data.record_count} records`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    }
  };

  const fetchDuplicateStats = async () => {
    setLoadingDuplicateStats(true);
    try {
      const response = await apiClient.get('/admin/all-usn-details/duplicates/stats');
      if (response.data.success) {
        setDuplicateStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching duplicate stats:', error);
      toast.error('Failed to load duplicate stats');
    } finally {
      setLoadingDuplicateStats(false);
    }
  };

  const handleOpenDuplicateDialog = () => {
    setDuplicateDialogOpen(true);
    fetchDuplicateStats();
  };

  const handleExportDuplicates = async (pageNum) => {
    setExportingDuplicates(true);
    setCurrentExportPage(pageNum);
    try {
      const response = await apiClient.get(`/admin/all-usn-details/duplicates/export?page=${pageNum}&page_size=100000`);
      
      if (response.data.success) {
        const blob = new Blob([response.data.csv_data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = response.data.filename || `duplicate_usns_page${pageNum}.csv`;
        link.click();
        toast.success(`Exported ${formatIndianNumber(response.data.record_count)} records (Page ${pageNum} of ${response.data.total_pages})`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    } finally {
      setExportingDuplicates(false);
      setCurrentExportPage(0);
    }
  };

  const handleExportAllDuplicates = async () => {
    if (!duplicateStats || duplicateStats.total_pages === 0) return;
    
    setExportingDuplicates(true);
    toast.info(`Starting export of ${duplicateStats.total_pages} files...`);
    
    for (let page = 1; page <= duplicateStats.total_pages; page++) {
      setCurrentExportPage(page);
      try {
        const response = await apiClient.get(`/admin/all-usn-details/duplicates/export?page=${page}&page_size=100000`);
        
        if (response.data.success) {
          const blob = new Blob([response.data.csv_data], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = response.data.filename || `duplicate_usns_page${page}.csv`;
          link.click();
          
          // Small delay between downloads to prevent browser issues
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Export error for page ${page}:`, error);
        toast.error(`Export failed for page ${page}`);
        break;
      }
    }
    
    setExportingDuplicates(false);
    setCurrentExportPage(0);
    toast.success(`Export complete! ${duplicateStats.total_pages} files downloaded`);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">All USN Details Report</h1>
                <p className="text-sm text-[#E8DCC8]">Consolidated USN information with lookups</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setMigrateDialogOpen(true)}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="migrate-btn"
            >
              <Database className="w-4 h-4 mr-2" />
              Run Migration
            </Button>
            <Button
              variant="outline"
              onClick={handleOpenDuplicateDialog}
              className="bg-orange-500/20 border-orange-300/50 text-white hover:bg-orange-500/30"
              data-testid="export-duplicates-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Export Duplicates
            </Button>
            <Button
              variant="outline"
              onClick={handleExport}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Cards */}
        {globalStats && (
          <>
            {/* Source breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Total Records</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{formatIndianNumber(globalStats.total_records)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Greige</p>
                  <p className="text-2xl font-bold text-blue-600">{formatIndianNumber(globalStats.by_source?.greige || 0)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">FOB</p>
                  <p className="text-2xl font-bold text-indigo-600">{formatIndianNumber(globalStats.by_source?.fob || 0)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">GT - Sort No</p>
                  <p className="text-2xl font-bold text-cyan-600">{formatIndianNumber(globalStats.by_source?.gt_sortno || 0)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">GT - FG</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatIndianNumber(globalStats.by_source?.gt_fg || 0)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">GT - Label</p>
                  <p className="text-2xl font-bold text-amber-600">{formatIndianNumber(globalStats.by_source?.gt_label || 0)}</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Quality metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Sync %</p>
                  <p className="text-2xl font-bold text-green-600">{globalStats.sync_percentage}%</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">With FG Barcode</p>
                  <p className="text-2xl font-bold text-purple-600">{formatIndianNumber(globalStats.with_fg_barcode)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Unique Designs</p>
                  <p className="text-2xl font-bold text-teal-600">{formatIndianNumber(globalStats.unique_designs)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Source Total</p>
                  <p className="text-2xl font-bold text-gray-600">{formatIndianNumber(globalStats.source_counts?.total || 0)}</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Hide' : 'Show'}
                </Button>
                <Button
                  onClick={handleSearch}
                  size="sm"
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  disabled={isLoading}
                  data-testid="search-btn"
                >
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">USN Code</label>
                  <Input
                    placeholder="Filter by USN..."
                    value={filters.usn_code}
                    onChange={(e) => setFilters(prev => ({ ...prev, usn_code: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Bundle Barcode</label>
                  <Input
                    placeholder="Filter by bundle..."
                    value={filters.bundle_barcode}
                    onChange={(e) => setFilters(prev => ({ ...prev, bundle_barcode: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Sort No</label>
                  <Input
                    placeholder="Filter by sort no..."
                    value={filters.sort_no}
                    onChange={(e) => setFilters(prev => ({ ...prev, sort_no: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">FG Barcode</label>
                  <Input
                    placeholder="Filter by FG barcode..."
                    value={filters.fg_barcode}
                    onChange={(e) => setFilters(prev => ({ ...prev, fg_barcode: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Design Name</label>
                  <Input
                    placeholder="Filter by design..."
                    value={filters.design_name}
                    onChange={(e) => setFilters(prev => ({ ...prev, design_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Shade</label>
                  <Input
                    placeholder="Filter by shade..."
                    value={filters.shade}
                    onChange={(e) => setFilters(prev => ({ ...prev, shade: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Size</label>
                  <Input
                    placeholder="Filter by size..."
                    value={filters.size}
                    onChange={(e) => setFilters(prev => ({ ...prev, size: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Source</label>
                  <Select
                    value={filters.source}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, source: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Sources" />
                    </SelectTrigger>
                    <SelectContent>
                      {sourceOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value || "all"}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Results Summary */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            Showing {formatIndianNumber(records.length)} of {formatIndianNumber(total)} records
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchStats(); fetchRecords(); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">USN Code</TableHead>
                    <TableHead className="font-semibold">Bundle Barcode</TableHead>
                    <TableHead className="font-semibold">Sort No</TableHead>
                    <TableHead className="font-semibold">FG Barcode</TableHead>
                    <TableHead className="font-semibold">Design Name</TableHead>
                    <TableHead className="font-semibold">Shade</TableHead>
                    <TableHead className="font-semibold">Size</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="font-semibold">Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No records found. Run migration to populate data.
                      </TableCell>
                    </TableRow>
                  ) : (
                    records.map((record, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-sm">{record.usn_code}</TableCell>
                        <TableCell className="font-mono text-sm">{record.bundle_barcode || '-'}</TableCell>
                        <TableCell>{record.sort_no || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {record.fg_barcode || <span className="text-gray-400">-</span>}
                        </TableCell>
                        <TableCell>
                          {record.design_name || <span className="text-gray-400">-</span>}
                        </TableCell>
                        <TableCell>{record.shade || '-'}</TableCell>
                        <TableCell>{record.size || '-'}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={record.description}>
                          {record.description || '-'}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            record.source === 'Greige' ? 'bg-blue-100 text-blue-800' : 
                            record.source === 'FOB' ? 'bg-indigo-100 text-indigo-800' : 
                            record.source === 'GT - Sort No' ? 'bg-cyan-100 text-cyan-800' :
                            record.source === 'GT - FG' ? 'bg-emerald-100 text-emerald-800' :
                            record.source === 'GT - Label' ? 'bg-amber-100 text-amber-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {record.source || '-'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination */}
            {total > limit && (
              <div className="flex justify-between items-center px-4 py-3 border-t">
                <Button
                  variant="outline"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page + 1} of {Math.ceil(total / limit)}
                </span>
                <Button
                  variant="outline"
                  disabled={(page + 1) * limit >= total}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Migration Dialog */}
      <Dialog open={migrateDialogOpen} onOpenChange={setMigrateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Run Migration
            </DialogTitle>
            <DialogDescription>
              This will sync all records from <strong>greige_usns</strong> to <strong>all_usn_details</strong> with enriched lookup data.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-2 text-sm">
            <p><strong>Source:</strong> greige_usns ({globalStats?.source_greige_usns_count?.toLocaleString() || 0} records)</p>
            <p><strong>Destination:</strong> all_usn_details ({globalStats?.total_records?.toLocaleString() || 0} existing records)</p>
            <p className="text-gray-500">
              Lookups will be performed to get FG Barcode from greige_sort_nos and design details from rates_master.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMigrateDialogOpen(false)} disabled={isMigrating}>
              Cancel
            </Button>
            <Button onClick={handleMigrate} disabled={isMigrating} className="bg-green-600 hover:bg-green-700">
              {isMigrating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Migrating...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Migration
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Export Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Export Duplicate USNs
            </DialogTitle>
            <DialogDescription>
              Export all USN records that appear more than once across all sources for data cleanup analysis.
            </DialogDescription>
          </DialogHeader>
          
          {loadingDuplicateStats ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Analyzing duplicates...</p>
            </div>
          ) : duplicateStats ? (
            <div className="py-4 space-y-4">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-600 font-medium">Unique Duplicate USNs</p>
                  <p className="text-2xl font-bold text-orange-700">{formatIndianNumber(duplicateStats.unique_duplicate_usns)}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600 font-medium">Total Duplicate Records</p>
                  <p className="text-2xl font-bold text-red-700">{formatIndianNumber(duplicateStats.total_duplicate_records)}</p>
                </div>
              </div>
              
              {/* Export Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Export Information</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Records per file: <strong>{formatIndianNumber(duplicateStats.page_size)}</strong></li>
                  <li>• Total files to download: <strong>{duplicateStats.total_pages}</strong></li>
                </ul>
              </div>
              
              {/* Export Options */}
              {duplicateStats.total_pages > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Download Options:</p>
                  
                  {/* Download All Button */}
                  <Button 
                    onClick={handleExportAllDuplicates} 
                    disabled={exportingDuplicates}
                    className="w-full bg-orange-600 hover:bg-orange-700"
                  >
                    {exportingDuplicates ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Exporting Page {currentExportPage} of {duplicateStats.total_pages}...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download All ({duplicateStats.total_pages} files)
                      </>
                    )}
                  </Button>
                  
                  {/* Individual Page Downloads */}
                  {duplicateStats.total_pages > 1 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Or download individual files:</p>
                      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                        {Array.from({ length: duplicateStats.total_pages }, (_, i) => i + 1).map((pageNum) => (
                          <Button
                            key={pageNum}
                            variant="outline"
                            size="sm"
                            onClick={() => handleExportDuplicates(pageNum)}
                            disabled={exportingDuplicates}
                            className="text-xs"
                          >
                            {exportingDuplicates && currentExportPage === pageNum ? (
                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3 mr-1" />
                            )}
                            Page {pageNum}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p>No duplicate USNs found!</p>
                </div>
              )}
            </div>
          ) : (
            <div className="py-4 text-center text-gray-500">
              <p>Failed to load duplicate statistics</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)} disabled={exportingDuplicates}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllUSNDetailsReport;
